import { Injectable, signal, computed, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { SignalRService } from '../signalr.service';
import { StorageService } from './storage.service';
import { ICurrentUserModel, ILoginResponseModel } from '@models/auth/auth.model';
import { PATIENT_CURRENT_USER, Routing, AppModule, IRolePermisssionModel } from '@medicalapp/shared-core';
import { PatientLegalAcceptanceService } from '@services/patient/patient-legal-acceptance.service';

const DEFAULT_PERMISSIONS: readonly IRolePermisssionModel[] = [
  { ModuleName: AppModule.Dashboard, CanManage: true, CanView: true, CanDelete: true },
  { ModuleName: AppModule.Messages, CanManage: true, CanView: true, CanDelete: true },
  { ModuleName: AppModule.Profile, CanManage: true, CanView: true, CanDelete: true },
  { ModuleName: AppModule.CarePlan, CanManage: true, CanView: true, CanDelete: true },
  { ModuleName: AppModule.Patient, CanManage: true, CanView: true, CanDelete: true },
];

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  public currentUser = signal<ICurrentUserModel | null>(null);
  public isLoggedIn = computed(() => !!this.currentUser()?.jwtToken);

  private storageService = inject(StorageService);
  private router = inject(Router);
  private injector = inject(Injector);
  private initPromise!: Promise<void>;

  constructor() {
    this.initPromise = this.loadInitialUser();
  }

  async getAsync(): Promise<ICurrentUserModel | null> {
    await this.initPromise;
    return this.currentUser();
  }

  private async loadInitialUser() {
    const user = await this.readFromStorage();
    // Only apply storage value if no in-memory user was set during the async gap
    // (e.g. when the user logs in before loadInitialUser completes)
    if (this.currentUser() === null) {
      this.currentUser.set(user);
    }
  }

  private async persist(currentUser: ICurrentUserModel) {
    await this.storageService.set(PATIENT_CURRENT_USER, JSON.stringify(currentUser));
  }

  private async readFromStorage(): Promise<ICurrentUserModel | null> {
    const val = await this.storageService.get(PATIENT_CURRENT_USER);
    if (!val) return null;
    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  }

  get(): ICurrentUserModel | null {
    return this.currentUser();
  }

  setCurrentUser(responseBody: ILoginResponseModel) {
    const permissions = this.getRolePermissions(responseBody?.permissions);

    const currentUser: ICurrentUserModel = {
      ...responseBody,
      expiration: typeof responseBody.expiration === 'string' ? new Date(responseBody.expiration) : responseBody.expiration,
      permissions,
    };
    this.currentUser.set(currentUser);
    this.persist(currentUser);
    try {
      this.injector.get(PatientLegalAcceptanceService).clearAcceptanceCache();
    } catch {
      // ignore
    }
  }

  /**
   * Patches only the token fields in the stored session after a silent refresh.
   * All other user fields (permissions, userName, etc.) remain unchanged.
   */
  updateTokens(jwtToken: string, refreshToken: string, expiration: Date): void {
    const current = this.currentUser();
    if (!current) return;
    const updated: ICurrentUserModel = { ...current, jwtToken, refreshToken, expiration };
    this.currentUser.set(updated);
    this.persist(updated);
  }

  getRolePermissions(permissionStr: string): IRolePermisssionModel[] {
    let permissions: IRolePermisssionModel[] = [];
    if (permissionStr) {
      try {
        permissions = JSON.parse(permissionStr);
      } catch {
        permissions = [];
      }
    }

    DEFAULT_PERMISSIONS.forEach((defaultPermission) => {
      const alreadyGranted = permissions.some((p) => p.ModuleName === defaultPermission.ModuleName);
      if (!alreadyGranted) permissions.push(defaultPermission);
    });

    return permissions;
  }

  getShortName(): string {
    const name = this.currentUser()?.userName || 'A B';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  getUserName(): string | undefined {
    return this.currentUser()?.userName;
  }

  setUserName(userName: string) {
    const currentUser = this.currentUser();
    if (!currentUser) return;
    const updated = { ...currentUser, userName };
    this.currentUser.set(updated);
    this.persist(updated);
  }

  async clearSession(): Promise<void> {
    // Synchronously clear signal first so guards see null immediately
    this.currentUser.set(null);
    // Stop SignalR
    try {
      const signalRService = this.injector.get(SignalRService);
      signalRService.stopConnection();
    } catch (err) {
      console.error('Error stopping SignalR connection on logout:', err);
    }
    // Async storage cleanup
    await this.storageService.remove(PATIENT_CURRENT_USER);
    try {
      this.injector.get(PatientLegalAcceptanceService).clearAcceptanceCache();
    } catch {
      // ignore
    }
  }

  logout = async () => {
    await this.clearSession();
    this.router.navigateByUrl(`/${Routing.Auth}/${Routing.Login}`);
  };
}